/**
 * Geração de PDF do relatório de tela. Lazy-load jspdf + jspdf-autotable —
 * fica fora do chunk inicial.
 */

/** Realce opcional por linha do corpo (Task #186 — DRE escalonado).
 * - `strong`   subtotais-chave (EBITDA, Lucro Líquido): banda turquesa, bold.
 * - `highlight` demais calculadas (Receita Líquida, Margem Bruta…): banda clara, bold.
 * - `section`  cabeçalho de seção (ex.: "INFORMATIVO (fora-DRE)"): banda azul.
 * - `muted`    linha informativa/placeholder (D11, R6 "Em breve"): cinza itálico. */
export type PdfRowStyle = "strong" | "highlight" | "section" | "muted";

export interface PdfTable {
  head: string[];
  body: Array<Array<string | number>>;
  /** Realce por linha, paralelo a `body` (mesmo índice). Linhas sem entrada
   * (ou `undefined`) usam o estilo padrão + zebra. Backward-compat: tabelas
   * que não passam isso (Fluxo, DRE Contábil) renderizam como antes. */
  rowStyles?: Array<PdfRowStyle | undefined>;
}

export interface PdfInput {
  filename: string; // sem extensão — será acrescentado .pdf
  title: string;
  subtitle?: string;
  filtros?: string[];
  /** Linha extra no rodapé (acima do rodapé padrão) — ex.: cobertura % +
   * total de linhas do DRE (#186). */
  footerNote?: string;
  tables: Array<PdfTable & { title?: string }>;
}

interface AutoTableCellHook {
  section: "head" | "body" | "foot";
  row: { index: number };
  cell: { styles: Record<string, unknown> };
}

function applyRowStyle(
  data: AutoTableCellHook,
  rowStyles: Array<PdfRowStyle | undefined>,
): void {
  if (data.section !== "body") return;
  const style = rowStyles[data.row.index];
  if (!style) return;
  const s = data.cell.styles;
  if (style === "strong") {
    s.fillColor = "#CFF6F1";
    s.textColor = "#00203F";
    s.fontStyle = "bold";
  } else if (style === "highlight") {
    s.fillColor = "#EAF0F4";
    s.textColor = "#00203F";
    s.fontStyle = "bold";
  } else if (style === "section") {
    s.fillColor = "#00203F";
    s.textColor = "#FFFFFF";
    s.fontStyle = "bold";
    s.fontSize = 8;
  } else if (style === "muted") {
    s.textColor = "#888888";
    s.fontStyle = "italic";
  }
}

function nowBr(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())} BRT`;
}

export async function gerarPdfRelatorio(input: PdfInput): Promise<void> {
  const { default: jsPDF } = await import("jspdf");
  await import("jspdf-autotable");
  type DocWithAutoTable = InstanceType<typeof jsPDF> & {
    autoTable: (opts: unknown) => void;
    lastAutoTable?: { finalY: number };
  };

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" }) as DocWithAutoTable;
  const pageW = doc.internal.pageSize.getWidth();

  // Cabeçalho
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor("#00203F");
  doc.text("INDRA ENERGIA · CORTEX", 40, 45);

  doc.setDrawColor("#00DAC8");
  doc.setLineWidth(1.5);
  doc.line(40, 55, pageW - 40, 55);

  // Título
  doc.setFontSize(16);
  doc.setTextColor("#00203F");
  doc.text(input.title, 40, 80);

  if (input.subtitle) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor("#666666");
    doc.text(input.subtitle, 40, 98);
  }

  doc.setFontSize(9);
  doc.setTextColor("#888888");
  doc.text(`Gerado em ${nowBr()}`, 40, input.subtitle ? 112 : 98);

  let y = input.subtitle ? 128 : 114;
  if (input.filtros && input.filtros.length > 0) {
    doc.text("Filtros: " + input.filtros.join(" · "), 40, y);
    y += 14;
  }

  // Tabelas
  for (const t of input.tables) {
    if (t.title) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor("#00203F");
      doc.text(t.title, 40, y);
      y += 10;
    }
    const rowStyles = t.rowStyles;
    doc.autoTable({
      startY: y,
      head: [t.head],
      body: t.body,
      theme: "plain",
      styles: { font: "helvetica", fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: "#00203F", textColor: "#FFFFFF", fontStyle: "bold" },
      alternateRowStyles: { fillColor: "#F5F7FA" },
      margin: { left: 40, right: 40 },
      ...(rowStyles
        ? { didParseCell: (d: AutoTableCellHook) => applyRowStyle(d, rowStyles) }
        : {}),
    });
    y = (doc.lastAutoTable?.finalY ?? y) + 20;
  }

  // Rodapé (cada página)
  const pageCount = doc.getNumberOfPages();
  const pageH = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor("#999999");
    doc.setFont("helvetica", "normal");
    if (input.footerNote) {
      doc.text(input.footerNote, 40, pageH - 36);
    }
    doc.text(
      `Gerado em ${nowBr()} · Cortex v1.1 · confidencial`,
      40,
      pageH - 24,
    );
    doc.text(`${i}/${pageCount}`, pageW - 40, pageH - 24, { align: "right" });
  }

  doc.save(`${input.filename}.pdf`);
}
